/* eslint-disable @next/next/no-img-element */
"use client";
import { Fragment, useState } from "react";
import { CN_PRODUCT } from "@/components/product/const";
import Image, { StaticImageData } from "next/image";
import ScoreRating from "@/components/customerReviews/scoreRating";
import SkuCoupon from "./skuCoupon";
import SelectModelGroup from "./selectModelGroup";
import BuyOrAddToCar from "./buyOrAddToCart";
import Assurance from "@/components/product/assurance";
import { JDIPrice } from "@/lib/jdiClientUtils";
import { IntlDialog } from "@/components/intlMui";
import { getInitCommentListPage } from "@/apis/service";
import AllCommentList from "@/components/customerReviews/allCommentList";
import ArrowIcon from "@/images/icon/arrow-default.svg";
import KlranaIcon from "@/images/icon/product-klrana.svg";
import { SkuDetailWrapper } from "@/styles/product";
import { ErrorBoundary } from "react-error-boundary";
import { useParams } from "next/navigation";
import KlranaDialog from "./klranaDialog";
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25217";
import { dataExpTagConfig } from "@/components/product/const";
import { useTranslation } from "@/i18n/client";
import AddressSelector from "./addressSelector";
import TipSvg from "@/assets/icons/dp-final-price-tip.svg";
import Popover from '@mui/material/Popover';
import FinalPriceDrawerContent from "./finalPriceDrawerContent";
// styles
import { styled as muiStyled } from "@mui/material/styles";

interface SkuDetailProps {
  floors: any;
  localInfo: any;
  autoClaimCoupon?: any;
}

const WidthArrowPopover = muiStyled(Popover)(({theme})=> ({
  marginTop: "10px",
  overflow: "visible",
  '& .MuiPopover-paper': {
    boxShadow: theme.shadows[4],
    overflow: 'visible',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: "-8px", // 调整箭头位置
      left: '50%', // 调整箭头位置
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: `8px solid ${theme.palette.background.paper}`,
      // boxShadow: 'var(--mui-shadows-8)' // 可选阴影
      filter: 'drop-shadow(0px -1px 1px rgba(0,0,0,0.12))'
    }
  }
}));
export default function SkuDetail(props: SkuDetailProps) {
  const { t } = useTranslation();
  const { floors, localInfo } = props || {};
  const title = floors?.productSkuNameFloor?.data["C-M#ViewTitleItem&core"]?.title;
  const productCommentSaleFloor = floors?.productCommentSaleFloor?.data;
  const productPriceFloor = floors?.productPriceFloor?.data;
  
  const { star, commentCount, starShow, saleAmount } = productCommentSaleFloor || {};
  const { 
    percentReductionDesc, 
    mainPrice, 
    crossOffPrice,
    finalPricePop,
  }: Record<string, any> = productPriceFloor?.["C-M#priceViewItem&priceInfo"] || {};
  // 排行榜数据
  const bestSellerFloor = floors?.productBestSellerFloor?.data;
  const rankText = bestSellerFloor?.rankText;
  const category = bestSellerFloor?.categoryName;
  const categoryPageUrl = bestSellerFloor?.destinationPageUrl;
  // 分期支付
  const productPaymentFloor = floors?.productPaymentFloor?.data;
  const payText = productPaymentFloor?.payText;
  const klranaContent = productPaymentFloor?.popUpText;

  const [allCommentInfo, setAllCommentInfo] = useState<any>();
  const { sku: skuId } = useParams<{ sku: string }>();
  const [open, setOpen] = useState<boolean>(false);
  const [openKlrana, setOpenKlrana] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const floorsWithSkuIdProps = {
    floors,
    skuId,
  }
  const openReviewListDialog = async () => {
    sendClickTrack.Productdetail_StarRating_Click({
      mainskuid: skuId,
      ratingNum: String(star),
      reviewsNum: String(commentCount),
      SalesNum: String(saleAmount),
    });
    const res = await getInitCommentListPage({ skuId });
    if (res.code === "200") {
      setAllCommentInfo(res?.data);
    }
    setOpen(true);
  };

  const closeReviewListDialog = () => {
    setOpen(false);
  };

  const gotoRankLandPage = () => {
    location.href = categoryPageUrl;
  };
  const handlePopoverClick = (event: React.MouseEvent<HTMLButtonElement>)=> {
    setAnchorEl(event.currentTarget);
  }

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const openKlranaDialog = (displayText: string) => {
    setOpenKlrana(true);
    sendClickTrack.Productdetail_Installment_Click({
      mainskuid: skuId,
      displayText,
    });
  };

  const getReviewsDesc = () => {
    return commentCount == "1" ? `${t("dp.commentCount.review", { count: 1 })}` : `${t("dp.commentCount.review", { count: 5 })}`;
  };
  const isPopoverOpen = Boolean(anchorEl);
  const id = isPopoverOpen ? 'simple-popover' : undefined;

  return (
    <SkuDetailWrapper>
      {
        !!title && <h1 className={CN_PRODUCT("sku-title")}>
          {title}
        </h1>
      }
      <ErrorBoundary fallback={null}>
        {(bestSellerFloor || commentCount || saleAmount) && (
          <div className={CN_PRODUCT("sku-auxiliary-info")}>
            {bestSellerFloor && (
              <div className={CN_PRODUCT("sku-best-seller")}>
                <span className="rank-text">{rankText}</span>
                <span className="category-text" onClick={gotoRankLandPage}>
                  {t("dp.in", {
                    cat: category,
                    interpolation: {
                      escapeValue: false,
                    },
                  })}
                </span>
                <ArrowIcon />
              </div>
            )}
            {productCommentSaleFloor && commentCount && (
              <div
                className={CN_PRODUCT("sku-score")}
                exptag={"exp|" + EXPOSURE_RECORD.Productdetail_StarRating_Expo}
                data-exptag-config={dataExpTagConfig}
                data-exptag-json-param={JSON.stringify({
                  mainskuid: skuId,
                  ratingNum: String(star),
                  reviewsNum: String(commentCount),
                  SalesNum: String(saleAmount),
                })}
              >
                <ScoreRating
                  starShow={starShow}
                  value={star}
                  extra={
                    saleAmount ? (
                      <>
                        <span className="sale-amount margin-left">{t("dp.itemSold", { saleAmount })}</span>
                        <span className="comment-count" onClick={openReviewListDialog}>
                          {commentCount} {getReviewsDesc()}
                          <ArrowIcon />
                        </span>
                      </>
                    ) : (
                      <span className="comment-count">
                        {commentCount} {getReviewsDesc()}
                        <ArrowIcon />
                      </span>
                    )
                  }
                />
              </div>
            )}
            {productCommentSaleFloor && !commentCount && saleAmount && (
              <div className={CN_PRODUCT("sku-score")}>
                <div className="sale-amount">{t("dp.itemSold", { saleAmount })}</div>
              </div>
            )}
            <IntlDialog
              onClose={closeReviewListDialog}
              aria-labelledby="customized-dialog-title"
              open={open}
              title="Reviews"
              content={<AllCommentList data={allCommentInfo} skuId={skuId} />}
              width={800}
              showButtonAction={false}
            />
          </div>
        )}
      </ErrorBoundary>
      {/* {价格} */}
      {mainPrice && (
        <div className={CN_PRODUCT("sku-price")}>
          { percentReductionDesc && (
            <div className={CN_PRODUCT("sku-price-origin")}>
              <JDIPrice 
                symbol={localInfo?.currencySymbol} 
                price={crossOffPrice} 
                uiType={2} 
                lineThrough 
                containerStyle={{ 
                  fontSize: "16px", 
                  lineHeight: "22px",
                  color:  "#1A1A1A",
                  fontWeight: 600
                }} />
            </div>
          )}
          <div className={CN_PRODUCT("sku-price-real")}>
            <JDIPrice
              symbol={localInfo?.currencySymbol}
              price={mainPrice}
              uiType={1}
              symbolStyle={{
                fontSize: "24px",
                lineHeight: "24px",
              }}
              integerStyle={{
                fontSize: "36px",
                lineHeight: "40px",
              }}
              containerStyle={{
                fontSize: "36px",
                lineHeight: "40px",
                color: percentReductionDesc ? "#CC0C1C" : "#1A1A1A",
                fontWeight: 700
              }}
            />
          </div>
          {
            percentReductionDesc && <div className={CN_PRODUCT("sku-price-tag")}>
              {percentReductionDesc}
            </div>
          }
           {finalPricePop?.detailList && <div className="final-price-info" >
            <TipSvg aria-describedby={id}  onClick={handlePopoverClick}/>
            <WidthArrowPopover
              id={id}
              open={isPopoverOpen}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <FinalPriceDrawerContent
                symbol={localInfo?.currencySymbol}
                {...finalPricePop}
                onConfirm={()=>{
                  handlePopoverClose();
                }}
                />
            </WidthArrowPopover>
          </div>}
        </div>
      )}

      {/* {分期支付} */}
      {productPaymentFloor && (
        <div
          className={CN_PRODUCT("sku-klrana")}
          onClick={() => openKlranaDialog(payText)}
          exptag={"exp|" + EXPOSURE_RECORD.Productdetail_Installment_Expo}
          data-exptag-config={dataExpTagConfig}
          data-exptag-json-param={JSON.stringify({ mainskuid: skuId, displayText: payText })}
        >
          <span dangerouslySetInnerHTML={{ __html: payText }} />
          <KlranaIcon />
        </div>
      )}
      <KlranaDialog open={openKlrana} closeFn={() => setOpenKlrana(false)} content={klranaContent} />

      {/* {优惠券 活动} */}
      <SkuCoupon 
        floors={floors} 
        skuId={skuId} 
        localInfo={localInfo}
      />

      <ErrorBoundary fallback={null}>
        <SelectModelGroup floors={floors} spuTitle={title} />
      </ErrorBoundary>
      <BuyOrAddToCar 
        floors={floors} 
        autoClaimCoupon={props?.autoClaimCoupon}
      />
      <AddressSelector {
        ...floorsWithSkuIdProps
        }
      />
      <Assurance {
        ...floorsWithSkuIdProps
      } />
    </SkuDetailWrapper>
  );
}
